import React, { useState, useEffect, ChangeEvent, FormEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "@/assets/css/style.css";
import "./AdYoutubeChannel.module.css";
import { useAppSelector } from "@/store/hooks";

const YoutubeChannelForm: React.FC = () => {
  const baseUrl = import.meta.env.VITE_API_BASE_URL;
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [image, setImage] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>("");

  // ✅ Form fields
  const [channel, setChannel] = useState("");
  const [subscribers, setSubscribers] = useState("");
  const [link, setLink] = useState("");

  // ✅ Validation errors
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [loading, setLoading] = useState(false);

  const { token } = useAppSelector((state) => state.auth);

  // ✅ Fetch existing data if editing
  useEffect(() => {
    if (id) {
      fetch(`${baseUrl}/youtube-channel/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((res) => res.json())
        .then((data) => {
          console.log("Fetched Youtube Channel Data for edit:", data);
          if (data) {
            setChannel(data?.data?.name || "");
            setSubscribers(data?.data?.subscribers || "");
            setLink(data?.data?.link || "");
            if (data?.data?.photo) setPreviewUrl(data?.data?.photo);
          }
        });
    }
  }, [id, token]);

  // ✅ Preview image
  useEffect(() => {
    if (image) {
      const url = URL.createObjectURL(image);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [image]);

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) setImage(e.target.files[0]);
  };

  // ✅ Validation function
  const validate = () => {
    const newErrors: { [key: string]: string } = {};

    if (!channel.trim()) newErrors.title = "Title is required";

    const subscribersStr = String(subscribers || "").trim();
    if (!subscribersStr) newErrors.subscribers = "Subscribers count is required";
    else if (isNaN(Number(subscribersStr)))
      newErrors.subscribers = "Subscribers must be a number";

    if (!link.trim()) newErrors.link = "Channel link is required";
    else if (!/^https?:\/\/.+/.test(link))
      newErrors.link = "Enter a valid URL (http/https)";

    if (!id && !image) newErrors.image = "Image is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };


  // ✅ Submit handler
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const formData = new FormData();
      if (image) formData.append("icon", image);
      formData.append("channelName", channel);
      formData.append("subscribers", subscribers);
      formData.append("link", link);

      const url = id
        ? `${baseUrl}/youtube-channel/${id}`
        : `${baseUrl}/youtube-channel/add_channel`;

      const method = id ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        body: formData,
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json();
      if (data) {
        alert(id ? "Youtube Channel updated!" : "Youtube Channel added!");
        navigate("/youtube-channels");
      } else {
        alert(data.message || "Something went wrong!");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="side-app">
      <div className="main-container container-fluid">
        {/* PAGE HEADER */}
        <div className="page-header">
          <h1 className="page-title">
            {id ? "Edit Youtube Channel" : "Add Youtube Channel"}
          </h1>
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="/">Home</a></li>
            <li className="breadcrumb-item active">Youtube Channels</li>
          </ol>
        </div>

        {/* FORM */}
        <div className="row">
          <div className="col-xl-12 col-md-12">
            <form className="card" onSubmit={handleSubmit}>
              <div className="card-body">
                {/* Upload Box */}
                <div
                  className="upload-box mb-4"
                  onClick={() => document.getElementById("fileInput")?.click()}
                  style={{
                    border: "2px dashed #bbb",
                    borderRadius: "12px",
                    padding: "30px",
                    textAlign: "center",
                    cursor: "pointer",
                    background: "#fafafa",
                  }}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (e.dataTransfer.files[0]) {
                      setImage(e.dataTransfer.files[0]);
                    }
                  }}
                >
                  {previewUrl ? (
                    <div>
                      <img
                        src={previewUrl}
                        alt="Preview"
                        className="img-fluid rounded shadow"
                        style={{ maxHeight: "200px", objectFit: "cover" }}
                      />
                      <button
                        type="button"
                        className="btn btn-sm btn-danger mt-3"
                        onClick={(e) => {
                          e.stopPropagation();
                          setImage(null);
                          setPreviewUrl("");
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <>
                      <p className="fw-bold mb-1">Drag & Drop your image here</p>
                      <p className="text-muted mb-0">or click to browse</p>
                      <small className="d-block mt-2">
                        Recommended: 180px * 180px
                      </small>
                      {errors.image && (
                        <small className="text-danger">{errors.image}</small>
                      )}
                    </>
                  )}
                  <input
                    id="fileInput"
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={handleFileChange}
                  />
                </div>

                {/* Title */}
                <div className="form-group">
                  <label className="form-label">Channel Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={channel}
                    onChange={(e) => setChannel(e.target.value)}
                  />
                  {errors.title && <small className="text-danger">{errors.title}</small>}
                </div>
                {/* Subscribers */}
                <div className="form-group">
                  <label className="form-label">Subscribers</label>
                  <input
                    type="number"
                    className="form-control"
                    value={subscribers}
                    onChange={(e) => setSubscribers(e.target.value)}
                  />
                  {errors.subscribers && <small className="text-danger">{errors.subscribers}</small>}
                </div>

                {/* Link */}
                <div className="form-group">
                  <label className="form-label">Channel Link</label>
                  <input
                    type="url"
                    className="form-control"
                    value={link}
                    onChange={(e) => setLink(e.target.value)}
                  />
                  {errors.link && <small className="text-danger">{errors.link}</small>}
                </div>

                {/* Submit */}
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? (id ? "Updating…" : "Uploading…") : id ? "Update" : "Submit"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default YoutubeChannelForm;
